import React, { useEffect, useState, useRef } from 'react';
import DoctorInfoService from '../doctorInfo/doctorInfoService';
import DoctorClockService from '../ClockInService/DoctorClockService';
import OfficeService from '../office/officeService';
import AuthService from '../Security/AuthService';
import './drDash.css';
import { Pencil } from 'lucide-react';

const DoctorDashboard = () => {
  const [doctor, setDoctor] = useState(null);
  const [doctorId, setDoctorId] = useState(null);
  const [officeName, setOfficeName] = useState('');
  const [todayLog, setTodayLog] = useState(null);
  const [logError, setLogError] = useState('');
  const [clockedIn, setClockedIn] = useState(false);
  const [onBreak, setOnBreak] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedDoctor, setEditedDoctor] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef();

  const doctorService = new DoctorInfoService();
  const clockService = new DoctorClockService();
  const authService = new AuthService();

  useEffect(() => {
    const id = localStorage.getItem('doctorId');
    setDoctorId(id);
    if (!id) return;

    const loadDoctor = async () => {
      try {
        const data = await doctorService.getDoctorById(id);
        setDoctor(data);
        setEditedDoctor(data);
        setNewUsername(data.username);

        if (data.officeId) {
          const office = await new OfficeService().getOfficeNameById(data.officeId);
          setOfficeName(office);
        }

        const status = await clockService.getClockStatus(id);
        setClockedIn(status.clockedIn);
      } catch (err) {
        console.error('Error loading doctor/dashboard:', err.message);
      }
    };

    loadDoctor();
  }, []);

  useEffect(() => {
    if (!doctorId) return;
    const fetchTodayLog = async () => {
      try {
        const log = await clockService.getTodayLog(doctorId);
        setTodayLog(log);
      } catch (err) {
        setLogError(err.message);
      }
    };
    fetchTodayLog();
  }, [doctorId]);

  const handleUpdateProfile = async () => {
    try {
      let imageUrl = doctor.doctorPicture;
      if (selectedImage && editedDoctor.username) {
        const fileName = selectedImage.name;
        imageUrl = `/images/doctor/${editedDoctor.username}/${fileName}`;

        const uploadResponse = await fetch(`/api/doctorinfo/upload-image?username=${editedDoctor.username}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${doctorService.getToken()}`
          },
          body: (() => {
            const formData = new FormData();
            formData.append('file', selectedImage);
            return formData;
          })()
        });

        if (!uploadResponse.ok) {
          const err = await uploadResponse.text();
          throw new Error('Image upload failed: ' + err);
        }
      }

      const detailsToUpdate = {
        firstName: editedDoctor.firstName,
        lastName: editedDoctor.lastName,
        bio: editedDoctor.bio,
        doctorPicture: imageUrl,
      };

      await doctorService.updateDoctorDetails(doctorId, null, detailsToUpdate);

      setDoctor({ ...editedDoctor, doctorPicture: imageUrl });
      setEditMode(false);
      setSelectedImage(null);
      showSuccess('Profile details updated successfully.');
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  const handleUpdateCredentials = async () => {
    try {
      if (!newUsername.trim() && !newPassword.trim()) {
        alert('Please enter a new username or password.');
        return;
      }

      await doctorService.updateDoctorAccount(doctorId, newUsername.trim(), newPassword.trim());
      await authService.editUser(newUsername.trim(), newPassword.trim(), 'DOCTOR');

      setDoctor(prev => ({ ...prev, username: newUsername.trim() }));
      setNewPassword('');
      showSuccess('Login credentials updated.');
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const ClockFace = () => {
    const [time, setTime] = useState(new Date());
    useEffect(() => {
      const interval = setInterval(() => setTime(new Date()), 1000);
      return () => clearInterval(interval);
    }, []);

    const hourDeg = (time.getHours() % 12) * 30 + time.getMinutes() * 0.5;
    const minuteDeg = time.getMinutes() * 6;
    const secondDeg = time.getSeconds() * 6;

    return (
      <div className="analog-clock">
        {[...Array(12)].map((_, i) => (
          <div key={i + 1} className="clock-number" data-number={i + 1}><span>{i + 1}</span></div>
        ))}
        <div className="clock-hand hour" style={{ transform: `rotate(${hourDeg}deg)` }} />
        <div className="clock-hand minute" style={{ transform: `rotate(${minuteDeg}deg)` }} />
        <div className="clock-hand second" style={{ transform: `rotate(${secondDeg}deg)` }} />
        <div className="clock-controls">
          {!clockedIn && <button className="clock-button" onClick={() => clockService.clockIn(doctorId).then(() => { setClockedIn(true); showSuccess('Clocked in.'); })}>Clock In</button>}
          {clockedIn && !onBreak && <button className="clock-button" onClick={() => clockService.startBreak(doctorId).then(() => setOnBreak(true))}>Break</button>}
          {onBreak && <button className="clock-button" onClick={() => clockService.endBreak(doctorId).then(() => setOnBreak(false))}>Resume</button>}
          {clockedIn && <button className="clock-button" onClick={() => clockService.clockOut(doctorId).then(() => { setClockedIn(false); showSuccess('Clocked out.'); })}>Clock Out</button>}
        </div>
      </div>
    );
  };

  return (
    <div className="container">
      <h1 className="title">Doctor Dashboard</h1>
      {successMessage && <div className="success-message">{successMessage}</div>}
      <div className="doctor-dashboard-layout">
        <div className="dashboard-left">
          <div className="doctor-card">
            <div className="edit-icon" onClick={() => setEditMode(!editMode)}><Pencil size={18} /></div>
            <img
              src={selectedImage ? URL.createObjectURL(selectedImage) : doctor?.doctorPicture || 'default-doctor.jpg'}
              alt="Profile"
              className="profile-picture"
            />
            {editMode && (
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setSelectedImage(e.target.files[0])}
                ref={fileInputRef}
              />
            )}
            <div className="doctor-info">
              {editMode ? (
                <>
                  <input value={editedDoctor.firstName} onChange={(e) => setEditedDoctor({ ...editedDoctor, firstName: e.target.value })} />
                  <input value={editedDoctor.lastName} onChange={(e) => setEditedDoctor({ ...editedDoctor, lastName: e.target.value })} />
                  <input disabled value={editedDoctor.specialty} />
                  <textarea value={editedDoctor.bio} onChange={(e) => setEditedDoctor({ ...editedDoctor, bio: e.target.value })} />
                  <button onClick={handleUpdateProfile}>Save Profile Info</button>
                  <br />
                  <input value={newUsername} onChange={(e) => setNewUsername(e.target.value)} placeholder="New Username" />
                  <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New Password" />
                  <button onClick={handleUpdateCredentials}>Update Credentials</button>
                  <br />
                  <button onClick={() => setEditMode(false)}>Cancel</button>
                </>
              ) : (
                <>
                  <h2>{doctor?.firstName} {doctor?.lastName}</h2>
                  <p><strong>Office:</strong> {officeName}</p>
                  <p><strong>Specialty:</strong> {doctor?.specialty}</p>
                  <p><strong>Bio:</strong> {doctor?.bio}</p>
                  <p><strong>Username:</strong> {doctor?.username}</p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="dashboard-right">
          <ClockFace />
          <div className="clock-history">
            <h3>Today’s Clock Log</h3>
            {logError && <p style={{ color: 'red' }}>{logError}</p>}
            {!todayLog?.clockIn ? (
              <p>No log for today yet.</p>
            ) : (
              <table className="clock-table">
                <tbody>
                  <tr><td>Clock In:</td><td>{new Date(todayLog.clockIn).toLocaleTimeString()}</td></tr>
                  <tr><td>Clock Out:</td><td>{todayLog.clockOut ? new Date(todayLog.clockOut).toLocaleTimeString() : '—'}</td></tr>
                  <tr><td>Total Worked:</td><td>{todayLog.totalWorked} minutes</td></tr>
                  {todayLog.breaks && todayLog.breaks.length > 0 && (
                    <tr><td>Breaks:</td><td>{todayLog.breaks.map((b, i) => (
                      <div key={i}>{new Date(b.breakStart).toLocaleTimeString()} → {b.breakEnd ? new Date(b.breakEnd).toLocaleTimeString() : 'Ongoing'}</div>
                    ))}</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorDashboard;
